import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Home, ChevronLeft, ChevronRight, ChevronDown, SplitSquareHorizontal } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { cn } from '@/shared/lib/cn';
import { ContextMenu, ContextMenuTrigger } from '@/shared/ui/ContextMenu';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/ui/DropdownMenu';
import { IconButton } from '@/shared/ui/IconButton';
import { useTabsStore } from './tabsStore';
import { tabLabel, type TabGroup, type TabModel } from './model/types';
import { tabStripSegments } from './lib/tabGroups';
import { groupPalette } from './lib/tabGroupColors';
import { useTabReorder } from './hooks/useTabReorder';
import { TabItem } from './ui/TabItem';
import { TabGroupChip } from './ui/TabGroupChip';
import { TabContextMenu } from './ui/TabContextMenu';
import { TabGroupContextMenu } from './ui/TabGroupContextMenu';
import { NavIcon } from '../navMenu/NavIcon';

/**
 * Çok sekmeli belge çubuğu — Chrome/Safari mantığı.
 *
 * Sekmeler küçülmez (sabit genişlik); taşma olunca sol/sağ kaydırma düğmeleri ve "tüm
 * sekmeler" listesi çıkar. Bunun üstüne tarayıcılardaki dört davranış var:
 *   - Sabitleme: sekme başa geçer, daralır, kapat düğmesi kaybolur.
 *   - Gruplar: renkli, adlandırılabilir, katlanabilir bloklar; üyeleri hep bitişik.
 *   - Yeniden adlandırma: çift tık (ya da F2) ile yerinde.
 *   - Sürükleyerek taşıma: grup bloğunun içine bırakılan sekme gruba katılır.
 *
 * Şerit düzeninin kuralları burada değil `lib/tabGroups.ts`'te; bu dosya yalnızca çizer.
 */
export function TabBar() {
  const { t } = useTranslation();
  const tabs = useTabsStore((s) => s.tabs);
  const groups = useTabsStore((s) => s.groups);
  const activeTabId = useTabsStore((s) => s.activeTabId);
  const splitTabId = useTabsStore((s) => s.splitTabId);
  const store = useTabsStore.getState();
  const navigate = useNavigate();

  const stripRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const [renamingTabId, setRenamingTabId] = useState<string | null>(null);
  const [renamingGroupId, setRenamingGroupId] = useState<string | null>(null);

  // Segmentler TÜM sekmelerden üretilir, katlanmış olanlar dahil: katlanmış grubun üyeleri
  // çizilmez ama grubun kendisi (çip) şeritte kalmalı, yoksa grup geri açılamaz.
  const segments = useMemo(() => tabStripSegments(tabs, groups), [tabs, groups]);

  const urlOf = (tab: TabModel | null) => (tab ? `/s/${tab.screenId ?? tab.uniqueKey}?tab=${tab.tabId}` : '/');

  const go = useCallback(
    (tab: TabModel | null) => {
      store.activate(tab?.tabId ?? null);
      navigate(urlOf(tab), { replace: true });
    },
    [navigate, store],
  );

  /**
   * Kapatmadan sonra adres çubuğunu kalan aktif sekmeye çevirir.
   *
   * Kapatma yalnızca depoyu değiştiriyordu; adres `/s/<kapatılan>` olarak kalıyor ve
   * sayfa yenilendiğinde AppShell o adresi derin link sayıp kapatılan sekmeyi geri
   * açıyordu. Sekme oturum deposundan gerçekten silinmişti — geri getiren adresti.
   */
  const closing = useMemo(() => {
    const sync = () => {
      const st = useTabsStore.getState();
      navigate(urlOf(st.tabs.find((t) => t.tabId === st.activeTabId) ?? null), { replace: true });
    };
    const after = <A extends unknown[]>(fn: (...args: A) => void) => (...args: A) => { fn(...args); sync(); };
    return {
      ...store,
      close: after(store.close),
      closeOthers: after(store.closeOthers),
      closeRight: after(store.closeRight),
      closeLeft: after(store.closeLeft),
      closeAll: after(store.closeAll),
      closeGroup: after(store.closeGroup),
    };
  }, [navigate, store]);

  const { drag, startDrag } = useTabReorder({ tabs, onMove: store.moveTab });

  const measure = useCallback(() => {
    const el = stripRef.current;
    if (!el) return;
    setOverflow({ left: el.scrollLeft > 2, right: el.scrollLeft + el.clientWidth < el.scrollWidth - 2 });
  }, []);

  useEffect(() => {
    const el = stripRef.current;
    if (!el) return;
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    el.addEventListener('scroll', measure, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', measure);
    };
  }, [measure, tabs.length]);

  // Aktif sekme görünür alana kaydırılır
  useEffect(() => {
    const el = stripRef.current?.querySelector<HTMLElement>(`[data-tab-id="${activeTabId}"]`);
    el?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
  }, [activeTabId, tabs.length]);

  const scrollBy = (dir: -1 | 1) => stripRef.current?.scrollBy({ left: dir * 240, behavior: 'smooth' });
  const showNav = overflow.left || overflow.right;

  const renderTab = (tab: TabModel) => (
    <ContextMenu key={tab.tabId}>
      <ContextMenuTrigger asChild>
        {/* Radix tetikleyiciye ref veriyor; TabItem memo'lu olduğu için sarmalayıcı şart. */}
        <div className="shrink-0">
          <TabItem
            tab={tab}
            active={tab.tabId === activeTabId}
            inSplit={tab.tabId === splitTabId}
            renaming={renamingTabId === tab.tabId}
            dragging={drag?.id === tab.tabId}
            onActivate={() => go(tab)}
            onClose={() => closing.close(tab.tabId)}
            onRenameStart={() => setRenamingTabId(tab.tabId)}
            onRenameCommit={(v) => {
              store.rename(tab.tabId, v);
              setRenamingTabId(null);
            }}
            onRenameCancel={() => setRenamingTabId(null)}
            onPointerDown={(e) => startDrag(tab.tabId, e)}
          />
        </div>
      </ContextMenuTrigger>
      <TabContextMenu
        tab={tab}
        groups={groups}
        inSplit={tab.tabId === splitTabId}
        // Yan panel ancak başka bir sekme aktifken anlamlı: sekme kendi yanında açılamaz.
        canSplit={tabs.length > 1 && tab.tabId !== activeTabId}
        onRenameStart={() => setRenamingTabId(tab.tabId)}
        actions={closing}
      />
    </ContextMenu>
  );

  const renderGroup = (group: TabGroup, groupTabs: TabModel[]) => {
    const palette = groupPalette(group.color);
    return (
      <div
        key={group.groupId}
        className="flex h-8 shrink-0 items-end gap-0.5 self-end rounded-t-lg px-1"
        // Grup bloğu, üyelerini gözle ayırt edilir bir kutuda tutar; üstteki ince çizgi
        // grubun rengini sekmelerin arkasında da görünür kılar.
        style={{
          backgroundColor: `color-mix(in srgb, ${palette.bg} 22%, transparent)`,
          boxShadow: `inset 0 2px 0 ${palette.bg}`,
        }}
      >
        <ContextMenu>
          <ContextMenuTrigger asChild>
            <div className="flex self-center">
              <TabGroupChip
                group={group}
                count={groupTabs.length}
                renaming={renamingGroupId === group.groupId}
                onToggle={() => store.toggleGroupCollapsed(group.groupId)}
                onRenameCommit={(v) => {
                  store.renameGroup(group.groupId, v.trim());
                  setRenamingGroupId(null);
                }}
                onRenameCancel={() => setRenamingGroupId(null)}
              />
            </div>
          </ContextMenuTrigger>
          <TabGroupContextMenu
            group={group}
            onRenameStart={() => setRenamingGroupId(group.groupId)}
            onColor={(c) => store.setGroupColor(group.groupId, c)}
            onToggleCollapsed={() => store.toggleGroupCollapsed(group.groupId)}
            onUngroup={() => store.ungroup(group.groupId)}
            onCloseGroup={() => closing.closeGroup(group.groupId)}
          />
        </ContextMenu>
        {group.collapsed ? null : groupTabs.map(renderTab)}
      </div>
    );
  };

  return (
    <div className="border-border bg-bg-muted flex h-9 items-end border-b">
      <button
        type="button"
        role="tab"
        aria-selected={activeTabId === null}
        onClick={() => go(null)}
        className={cn(
          'ml-1 flex h-8 shrink-0 items-center rounded-t-md px-3 text-sm',
          activeTabId === null ? 'bg-bg-elevated text-fg' : 'text-fg-muted hover:bg-bg-hover',
        )}
      >
        <Home className="h-4 w-4" />
      </button>
      {showNav ? (
        <IconButton
          label={t('shell.tab.scrollLeft')}
          size="sm"
          onClick={() => scrollBy(-1)}
          disabled={!overflow.left}
          className="mb-0.5 shrink-0"
        >
          <ChevronLeft className="h-4 w-4" />
        </IconButton>
      ) : null}
      <div
        ref={stripRef}
        role="tablist"
        aria-label={t('shell.tabs')}
        className="flex min-w-0 flex-1 [scrollbar-width:none] items-end gap-0.5 overflow-x-auto px-0.5 [&::-webkit-scrollbar]:hidden"
      >
        {segments.map((seg) => (seg.kind === 'tab' ? renderTab(seg.tab) : renderGroup(seg.group, seg.tabs)))}
      </div>
      {/* Bırakma çizgisi: şeridin dışına taşmasın diye sabit konumlu ve yalnızca sürüklerken. */}
      {drag?.indicatorX != null ? (
        <div
          aria-hidden
          className="bg-brand-500 pointer-events-none fixed z-50 w-0.5"
          style={{ left: drag.indicatorX - 1, top: drag.indicatorTop, height: drag.indicatorHeight }}
        />
      ) : null}
      {showNav ? (
        <IconButton
          label={t('shell.tab.scrollRight')}
          size="sm"
          onClick={() => scrollBy(1)}
          disabled={!overflow.right}
          className="mb-0.5 shrink-0"
        >
          <ChevronRight className="h-4 w-4" />
        </IconButton>
      ) : null}
      {tabs.length > 0 ? (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <IconButton label={t('shell.tabs')} size="sm" className="mr-1 mb-0.5 shrink-0">
              <ChevronDown className="h-4 w-4" />
            </IconButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="max-h-[60vh] w-72 overflow-y-auto">
            {/* Katlanmış grup üyeleri şeritte görünmüyor; listede görünmeleri gerekiyor,
                yoksa katlanan grup sekmeleri erişilemez hale geliyor. */}
            {tabs.map((tab) => {
              const group = tab.groupId ? groups.find((g) => g.groupId === tab.groupId) : undefined;
              return (
                <DropdownMenuItem
                  key={tab.tabId}
                  onSelect={() => go(tab)}
                  className={cn(tab.tabId === activeTabId && 'text-brand-600 font-semibold')}
                >
                  {group ? (
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-full"
                      style={{ backgroundColor: groupPalette(group.color).bg }}
                    />
                  ) : null}
                  <NavIcon
                    node={{
                      iconId: tab.iconId ?? null,
                      screenTypeName: tab.screenTypeName ?? null,
                      children: [],
                    }}
                    className="h-3.5 w-3.5"
                  />
                  <span className="truncate">{tabLabel(tab)}</span>
                  {tab.tabId === splitTabId ? (
                    <SplitSquareHorizontal className="text-brand-600 ml-auto h-3.5 w-3.5" />
                  ) : null}
                </DropdownMenuItem>
              );
            })}
            <DropdownMenuSeparator />
            <DropdownMenuLabel>{t('shell.tab.sort.label')}</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => store.sortBy('caption')}>
              {t('shell.tab.sort.caption')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => store.sortBy('created')}>
              {t('shell.tab.sort.created')}
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => store.sortBy('recent')}>
              {t('shell.tab.sort.recent')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => closing.closeAll()}>{t('shell.tab.closeAll')}</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : null}
    </div>
  );
}
